import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const NAV_LINKS = [
  { label: 'Home', href: '/' },
  { label: 'Services', href: '/services/office-cleaning' },
  { label: 'About', href: '/about' },
  { label: 'Service Areas', href: '/locations/metairie-la' },
  { label: 'Contact', href: '/contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === '/';

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${
          scrolled
            ? 'bg-navy/98 backdrop-blur-md border-b border-white/5'
            : isHome
            ? 'bg-transparent'
            : 'bg-navy'
        }`}
        style={{ transitionDuration: '400ms' }}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 shrink-0 group">
            <div className={`rounded-xl p-1.5 transition-colors duration-300 ${scrolled || !isHome ? 'bg-white/10' : 'bg-white/15'}`}>
              <img
                src="https://ojsjanitorial.net/wp-content/uploads/2023/04/logo.png"
                alt="OJ's Janitorial & Sweeping Service, LLC"
                className="h-10 w-auto object-contain"
              />
            </div>
            <div className="hidden sm:block">
              <div className="text-white font-heading font-extrabold text-sm leading-tight tracking-tight">OJ&apos;s Janitorial</div>
              <div className="text-accent text-xs font-body tracking-wide">&amp; Sweeping Service, LLC</div>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm font-body font-medium px-4 py-2 rounded-full transition-all duration-200 whitespace-nowrap ${
                  location.pathname === link.href
                    ? 'bg-white/10 text-accent'
                    : 'text-white/75 hover:text-white hover:bg-white/8'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href="tel:+15048183347"
              className="flex items-center gap-2 text-white/75 hover:text-white text-sm font-body transition-colors whitespace-nowrap"
            >
              <span className="w-7 h-7 flex items-center justify-center bg-accent/15 rounded-lg">
                <i className="ri-phone-fill text-accent text-sm"></i>
              </span>
              <span className="font-semibold">(504) 818-3347</span>
            </a>
            <Link
              to="/contact"
              className="bg-accent hover:bg-accent-dark text-navy font-heading font-extrabold text-sm px-6 py-2.5 rounded-full transition-all duration-200 whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-file-text-line text-sm"></i>
              Get a Quote
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            className="lg:hidden text-white p-2 cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle mobile menu"
          >
            <i className={`text-2xl transition-all duration-200 ${mobileOpen ? 'ri-close-line' : 'ri-menu-3-line'}`}></i>
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-navy pt-20 overflow-y-auto">
          <nav className="flex flex-col px-6 pt-6 gap-1">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`font-body text-lg py-3.5 px-4 rounded-xl transition-all ${
                  location.pathname === link.href
                    ? 'bg-accent/10 text-accent font-semibold'
                    : 'text-white/80 hover:text-white hover:bg-white/5'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="mt-6 flex flex-col gap-3 border-t border-white/10 pt-6">
              <a
                href="tel:+15048183347"
                className="flex items-center justify-center gap-2 text-white font-body font-semibold text-base py-3.5 border border-white/15 rounded-xl"
              >
                <i className="ri-phone-fill text-accent"></i>
                (504) 818-3347
              </a>
              <Link
                to="/contact"
                className="bg-accent text-navy font-heading font-extrabold text-base py-3.5 rounded-full text-center whitespace-nowrap"
              >
                Get a Free Quote
              </Link>
            </div>
          </nav>
        </div>
      )}

      {/* Mobile floating call button */}
      <a
        href="tel:+15048183347"
        className="fixed bottom-6 right-6 z-50 lg:hidden bg-accent text-navy rounded-full w-14 h-14 flex items-center justify-center"
        aria-label="Call OJS Janitorial"
        style={{ boxShadow: '0 0 20px rgba(0,230,122,0.4)' }}
      >
        <i className="ri-phone-fill text-xl"></i>
      </a>
    </>
  );
}
